import React, {useEffect, useState} from "react";
import {Button, Form, Input, Modal, Space, Table, Tooltip, Upload, message} from "antd";
import type {UploadFile} from "antd";
import {
    UploadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DownloadOutlined
} from "@ant-design/icons";
import {ColumnsType} from "antd/es/table";
import {DocumentDto} from "../../../model/models";
import {HttpService} from "../../../services/http/Http.service";
import dayjs from "dayjs";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import 'react-quill/dist/quill.snow.css';
import ReactQuill from "react-quill";

export default function ModuliPage() {
    const [moduli, setModuli] = useState<DocumentDto[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDocument, setEditingDocument] = useState<DocumentDto | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchModuli();
    }, []);

    const fetchModuli = async () => {
        try {
            const data = await HttpService.getInstanceAxios().get("/document/allDocuments")
            setModuli(data.data)
        } catch (error) {
            HttpErrorService.handle("Errore nel recupero dei moduli", error, true)
        }
    };

    const handleUpload = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();
            const file = fileList[0]?.originFileObj;

            if (!file && !editingDocument) {
                message.error("Devi selezionare un file!");
                return;
            }

            if (file) formData.append("file", file);
            formData.append("name", values.name);
            formData.append("email_subject", values.email_subject);
            formData.append("email_body", values.email_body);

            const endpoint = editingDocument
                ? `/document/updateModule/${editingDocument.id}`
                : "/document/uploadModule";

            await HttpService.getInstanceAxios().post(endpoint, formData, {
                headers: {"Content-Type": "multipart/form-data"},
            })
            message.success(editingDocument ? "Modulo aggiornato con successo" : "Modulo caricato con successo");
            setModalVisible(false);
            setFileList([]);
            form.resetFields();
            setEditingDocument(null);
            fetchModuli();
        } catch (error) {
            HttpErrorService.handle("Errore durante il salvataggio del modulo", error, true)
        }

    };

    const handleDelete = async (id: number) => {
        try {
            await HttpService.getInstanceAxios().delete(`/document/delete/${id}`)
            message.success("Modulo eliminato");
            fetchModuli()
        } catch (error) {
            HttpErrorService.handle("Errore durante l'eliminazione del modulo", error, true)
        }
    };

    const handlePreview = async (id: number) => {
        try {
            const response = await HttpService.getInstanceAxios().get(`/document/${id}`, {
                responseType: "blob",
            });

            const contentType = response.headers["content-type"];
            const disposition = response.headers["content-disposition"];
            const matches = disposition && disposition.match(/filename="(.+)"/);
            const filename = matches ? decodeURIComponent(matches[1]) : `documento.${contentType.split("/")[1] || "bin"}`;

            const blob = new Blob([response.data], {type: contentType});
            const blobUrl = URL.createObjectURL(blob);

            const inlineTypes = ["application/pdf"];

            if (inlineTypes.includes(contentType)) {
                setPreviewUrl(blobUrl);
            } else {
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero del documento", error, true)
        }
    };

    useEffect(() => {
        if (modalVisible && editingDocument) {
            form.setFieldsValue({
                name: editingDocument.name,
                email_subject: editingDocument.emailSubject,
                email_body: editingDocument.emailBody
            });
        }
    }, [modalVisible, editingDocument]);

    const columns: ColumnsType<DocumentDto> = [
        {
            title: "Nome file",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Oggetto email",
            dataIndex: "emailSubject",
            key: "emailSubject",
        },
        {
            title: "Ultima modifica",
            key: "lastModified",
            render: (_, record) => {
                const date = record.updatedAt || record.createdAt;
                const tooltip = record.updatedAt
                    ? `Ultimo aggiornamento: ${dayjs(record.updatedAt).format("DD/MM/YYYY HH:mm")}`
                    : `Creato il: ${dayjs(record.createdAt).format("DD/MM/YYYY HH:mm")}`;
                return <Tooltip title={tooltip}>{dayjs(date).format("DD/MM/YYYY")}</Tooltip>;
            }
        },
        {
            title: "Aggiornato da",
            key: "user",
            render: (_, record) => record.updatedBy?.name ? record.updatedBy?.name + " " + record.updatedBy?.lastname : record.createdBy?.name + " " + record.createdBy?.lastname
        },
        {
            title: "Azioni",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<DownloadOutlined/>}
                        type="primary"
                        onClick={() => handlePreview(record.id)}
                    >
                    </Button>
                    <Button
                        icon={<EditOutlined/>}
                        type="primary"
                        onClick={() => {
                            setEditingDocument(record);
                            setModalVisible(true);
                        }}
                    >
                    </Button>
                    <Button
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => handleDelete(record.id)}
                    >
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <>
            <div style={{display: "flex", justifyContent: "flex-end", marginBottom: 20}}>
                <Button
                    type={"primary"}
                    icon={<PlusOutlined></PlusOutlined>}
                    onClick={() => {
                        setEditingDocument(null);
                        form.resetFields()
                        setModalVisible(true);
                    }}
                >
                    Nuovo modulo
                </Button>
            </div>

            <Table dataSource={moduli} columns={columns} rowKey="id"/>

            <Modal
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingDocument(null);
                    form.resetFields()
                }}
                title={editingDocument ? "Modifica modulo" : "Carica nuovo modulo"}
                onOk={handleUpload}
                okText={editingDocument ? "Salva" : "Carica"}
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="Seleziona File" required>
                        <Upload
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={({fileList}) => {
                                const newFileList = fileList.slice(-1);
                                setFileList(newFileList);
                                if (newFileList.length > 0 && newFileList[0].name) {
                                    const filename = newFileList[0].name;
                                    const nameWithoutExt = filename.split(".").slice(0, -1).join(".");
                                    form.setFieldsValue({name: nameWithoutExt});
                                }
                            }}
                            multiple={false}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined/>}>Seleziona file</Button>
                        </Upload>
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Nome"
                        rules={[{required: true, message: "Inserisci il nome", max:64}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="email_subject"
                        label="Oggetto email"
                        rules={[{required: true, message: "Inserisci l'oggetto dell'email"}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="email_body"
                        label="Corpo email"
                        rules={[{required: true, message: "Inserisci il corpo dell'email"}]}
                    >
                        <ReactQuill theme="snow" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={!!previewUrl}
                onCancel={() => setPreviewUrl(null)}
                footer={null}
                width="80%"
                style={{top: 20}}
                title="Anteprima Documento"
            >
                {previewUrl && (
                    <iframe
                        src={previewUrl}
                        title="Anteprima documento"
                        width="100%"
                        height="600px"
                        style={{border: "none"}}
                    />
                )}
            </Modal>
        </>
    );
}
